import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  InputAdornment,
  Paper,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
  PERSONAL_ALLOWANCE,
  TAPER_THRESHOLD,
  ADDITIONAL_RATE_THRESHOLD,
  BASIC_BAND,
  BASIC_RATE,
  HIGHER_RATE,
  ADDITIONAL_RATE,
  personalAllowanceForIncome,
  calculateTax,
} from './allowanceTrapUtils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(value || 0);

const parseNumberInput = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return 0;
  const cleaned = value.replace(/,/g, '');
  const parsed = parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};


const INCOME_MIN = 95000;
const INCOME_MAX = 140000;
const INCOME_STEP = 250;

const AllowanceTrapApp = () => {
  const [income, setIncome] = useState(100000);
  const [pensionContribution, setPensionContribution] = useState(0);

  const results = useMemo(
    () => calculateTax(income, pensionContribution),
    [income, pensionContribution]
  );

  const withoutTaper = useMemo(
    () => calculateTax(income, pensionContribution, { ignoreTaper: true }),
    [income, pensionContribution]
  );

  const baselineNoPension = useMemo(
    () => calculateTax(income, 0),
    [income]
  );

  const taperPenalty = useMemo(
    () => Math.max(0, results.totalTax - withoutTaper.totalTax),
    [results.totalTax, withoutTaper.totalTax]
  );

  const taperTakeHomeGap = useMemo(
    () => Math.max(0, withoutTaper.takeHome - results.takeHome),
    [results.takeHome, withoutTaper.takeHome]
  );

  const pensionComparison = useMemo(() => {
    const pension = pensionContribution;
    if (pension <= 0) {
      return {
        taxSaved: 0,
        takeHomeDrop: 0,
        totalKeptWithPension: results.takeHome,
        totalKeptWithout: baselineNoPension.takeHome,
        netBetterOff: 0,
      };
    }
    const taxSaved = Math.max(0, baselineNoPension.totalTax - results.totalTax);
    const takeHomeDrop = Math.max(0, baselineNoPension.takeHome - results.takeHome);
    const totalKeptWithPension = results.takeHome + pension;
    const totalKeptWithout = baselineNoPension.takeHome;
    const netBetterOff = totalKeptWithPension - totalKeptWithout;
    return {
      taxSaved,
      takeHomeDrop,
      totalKeptWithPension,
      totalKeptWithout,
      netBetterOff,
    };
  }, [baselineNoPension.takeHome, baselineNoPension.totalTax, pensionContribution, results.takeHome, results.totalTax]);

  const deltaSpan = 10000;
  const lowerIncome = Math.max(INCOME_MIN, income - deltaSpan);
  const lowerCalc = useMemo(() => calculateTax(lowerIncome, pensionContribution), [lowerIncome, pensionContribution]);
  const lowerNoPension = useMemo(() => calculateTax(lowerIncome, 0), [lowerIncome]);
  const gainFromDelta = results.takeHome - lowerCalc.takeHome;
  const gainPercent = deltaSpan > 0 ? (gainFromDelta / deltaSpan) * 100 : 0;
  const gainFromDeltaNoPension = baselineNoPension.takeHome - lowerNoPension.takeHome;
  const gainPercentNoPension = deltaSpan > 0 ? (gainFromDeltaNoPension / deltaSpan) * 100 : 0;

  const pensionToEscapeTrap = Math.max(0, results.adjustedIncome - TAPER_THRESHOLD);
  const escapeCalc = useMemo(
    () => calculateTax(income, Math.min(pensionToEscapeTrap, income)),
    [income, pensionToEscapeTrap]
  );
  const escapeTotalKept = escapeCalc.takeHome + Math.min(pensionToEscapeTrap, income);

  const marginalRate = useMemo(() => {
    const delta = 100;
    const current = calculateTax(income, pensionContribution);
    const next = calculateTax(income + delta, pensionContribution);
    const marginalTax = next.totalTax - current.totalTax;
    const rate = (marginalTax / delta) * 100;
    return Math.max(0, Math.min(100, rate));
  }, [income, pensionContribution]);

  const rangeData = useMemo(() => {
    const start = INCOME_MIN;
    const end = INCOME_MAX;
    const step = 2500;
    const points = [];
    for (let current = start; current <= end; current += step) {
      const pension = Math.min(Math.max(pensionContribution, 0), current);
      const withPension = calculateTax(current, pension);
      const withoutPension = calculateTax(current, 0);
      points.push({
        gross: current,
        withPensionNet: withPension.takeHome,
        withPensionTotalKept: withPension.takeHome + pension,
        withoutPensionNet: withoutPension.takeHome,
      });
    }
    return points;
  }, [pensionContribution]);

  const nearestPoint = useMemo(() => {
    return rangeData.reduce(
      (acc, point) => {
        const diff = Math.abs(point.gross - income);
        if (diff < acc.diff) return { diff, point };
        return acc;
      },
      { diff: Infinity, point: null }
    ).point;
  }, [income, rangeData]);

  const lineData = useMemo(
    () => ({
      labels: rangeData.map((point) => `£${(point.gross / 1000).toFixed(0)}k`),
      datasets: [
        {
          label: 'Take-home (no pension)',
          data: rangeData.map((point) => point.withoutPensionNet),
          borderColor: '#ef6c00',
          backgroundColor: 'rgba(239, 108, 0, 0.08)',
          fill: true,
          tension: 0.35,
        },
        {
          label: 'Take-home (with pension)',
          data: rangeData.map((point) => point.withPensionNet),
          borderColor: '#1976d2',
          backgroundColor: 'rgba(25, 118, 210, 0.14)',
          fill: true,
          tension: 0.35,
        },
        {
          label: 'Take-home + pension (kept by you)',
          data: rangeData.map((point) => point.withPensionTotalKept),
          borderColor: '#2e7d32',
          backgroundColor: 'rgba(46, 125, 50, 0.12)',
          borderDash: [6, 6],
          fill: false,
          tension: 0.35,
        },
        {
          label: 'Your salary',
          data: rangeData.map((point) =>
            nearestPoint && point.gross === nearestPoint.gross ? point.withPensionNet : null
          ),
          borderColor: '#8e24aa',
          backgroundColor: '#8e24aa',
          pointRadius: 7,
          pointHoverRadius: 9,
          showLine: false,
        },
      ],
    }),
    [rangeData, nearestPoint]
  );

  const lineOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'top' },
        tooltip: {
          callbacks: {
            label: (context) => `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`,
          },
        },
      },
      scales: {
        y: {
          ticks: {
            callback: (value) => `£${(value / 1000).toFixed(0)}k`,
          },
        },
      },
    }),
    []
  );

  const handleIncomeInput = (val) => {
    const parsed = parseNumberInput(val);
    setIncome(Math.min(Math.max(parsed, INCOME_MIN), INCOME_MAX));
  };

  const handlePensionInput = (val) => {
    const parsed = parseNumberInput(val);
    setPensionContribution(Math.max(0, parsed));
  };

  const sliderValue = (val) => (Array.isArray(val) ? val[0] : val);

  const adjustedNetIncome = results.adjustedIncome;
  const inTrapBand =
    adjustedNetIncome >= TAPER_THRESHOLD && adjustedNetIncome < ADDITIONAL_RATE_THRESHOLD;
  const incomeMarks = [
    { value: TAPER_THRESHOLD, label: '£100k' },
    { value: ADDITIONAL_RATE_THRESHOLD, label: '£125k' },
  ];

  return (
    <>
      <Helmet>
        <title>£100k to £125k Personal Allowance Trap | Talk About Finance</title>
        <meta
          name="description"
          content="See how the personal allowance taper creates a 60% marginal tax rate between £100k and £125k, and how pension contributions can soften the hit."
        />
        <link rel="canonical" href="https://talkabout.finance/apps/100k-125k-personal-allowance-tax-trap" />
      </Helmet>

      <Container sx={{ mt: 6, mb: 8, pb: 6 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 4,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: (theme) => theme.shadows[1],
            background: (theme) =>
              theme.palette.mode === 'light'
                ? 'linear-gradient(135deg, #f1f6ff 0%, #ffffff 45%, #eef3ff 100%)'
                : 'linear-gradient(135deg, #1b2433 0%, #121725 45%, #1b2433 100%)',
          }}
        >
          <Stack spacing={1.5} sx={{ flex: 1, minWidth: 280, width: '100%' }}>
            <Box>
              <Button variant="outlined" color="primary" component={RouterLink} to="/apps">
                Back to apps
              </Button>
            </Box>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
              Calculator
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              £100k to £125k Personal Allowance Trap
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '100%' }}>
              Between £100k and £125k the personal allowance is withdrawn £1 for every £2 earned,
              making the marginal rate jump to 60%. Move the sliders to see the spike and how
              pension or gift-aid contributions can bring your adjusted income back under £100k.
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label="60% marginal band"
                color="warning"
                sx={{ fontWeight: 700, textTransform: 'uppercase' }}
              />
              <Typography variant="body2" color="text.secondary">
                Personal allowance tapers away here.
              </Typography>
            </Stack>
          </Stack>
        </Paper>

        <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, pb: { xs: 5, md: 6 }, mb: 4, borderRadius: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Gross salary
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {formatCurrency(income)}
                </Typography>
              </Stack>
              <Slider
                value={income}
                min={INCOME_MIN}
                max={INCOME_MAX}
                step={INCOME_STEP}
                marks={incomeMarks}
                onChange={(_, value) => setIncome(sliderValue(value))}
                sx={{ mt: 2, mb: 3 }}
              />
              <TextField
                value={income.toLocaleString('en-GB')}
                onChange={(e) => handleIncomeInput(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment>, inputMode: 'decimal' }}
                fullWidth
                label="Or type an exact salary"
                sx={{ mt: 1 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Pension / Gift Aid (pre-tax)
              </Typography>
              <Slider
                value={Math.min(pensionContribution, 40000)}
                min={0}
                max={40000}
                step={500}
                onChange={(_, value) => setPensionContribution(sliderValue(value))}
                sx={{ mb: 1 }}
              />
              <TextField
                value={pensionContribution.toLocaleString('en-GB')}
                onChange={(e) => handlePensionInput(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment>, inputMode: 'decimal' }}
                fullWidth
                label="Reduce adjusted income by"
              />
              <Typography variant="caption" color="text.secondary">
                Salary sacrifice, personal pension payments, or grossed-up Gift Aid can lower your adjusted
                income and restore the allowance.
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            spacing={2.5}
            sx={{ mt: { xs: 2.5, md: 3 }, mb: { xs: 1, md: 1.5 } }}
          >
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                variant="outlined"
                sx={{ p: 2.5, borderRadius: 3, height: { xs: 'auto', md: '100%' } }}
              >
                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Take-home after tax
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    {formatCurrency(results.takeHome)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Effective tax rate: {results.effectiveRate.toFixed(1)}%
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                variant="outlined"
                sx={{ p: 2.5, borderRadius: 3, height: { xs: 'auto', md: '100%' } }}
              >
                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total tax paid
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    {formatCurrency(results.totalTax)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Basic + higher + additional rates
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                variant="outlined"
                sx={{ p: 2.5, borderRadius: 3, height: { xs: 'auto', md: '100%' } }}
              >
                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Pension / Gift Aid
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    {formatCurrency(pensionContribution)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Reduces adjusted income
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                variant="outlined"
                sx={{ p: 2.5, borderRadius: 3, height: { xs: 'auto', md: '100%' } }}
              >
                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Marginal tax on the next £1
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: inTrapBand ? 'warning.main' : 'text.primary' }}>
                    {marginalRate.toFixed(0)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {inTrapBand
                      ? '40% higher-rate + 20% allowance withdrawal.'
                      : 'Outside the taper band.'}
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, pb: { xs: 5, md: 6 }, mt: 3, borderRadius: 3 }}>
          <Stack spacing={1.5}>
            <Typography variant="h6">The trap in plain English</Typography>
            <Typography variant="body2" color="text.secondary">
              Earning more in the £100k to £125k band barely moves your pay. Sacrificing that pay into pension/gift-aid keeps it for you and stops the allowance from disappearing.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              *Pension/Gift Aid figure is the amount to bring adjusted net income back to £100k. Salary sacrifice usually gives the cleanest result (and can also save NI).
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: '1fr',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
                gap: { xs: 2, sm: 2.5 },
                alignItems: 'stretch',
              }}
            >
              <Paper
                variant="outlined"
                sx={{ p: 2, height: { xs: 'auto', md: '100%' }, display: 'flex', flexDirection: 'column' }}
              >
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Extra £10k in this band
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    You keep {formatCurrency(gainFromDelta)} ({gainPercent.toFixed(0)}%)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Without pension: {formatCurrency(gainFromDeltaNoPension)} ({gainPercentNoPension.toFixed(0)}%)
                  </Typography>
                </Stack>
              </Paper>
              <Paper
                variant="outlined"
                sx={{ p: 2, height: { xs: 'auto', md: '100%' }, display: 'flex', flexDirection: 'column' }}
              >
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2" color="text.secondary">
                    To escape the trap
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Pension {formatCurrency(pensionToEscapeTrap)}*
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Then you keep {formatCurrency(escapeTotalKept)} (take-home + pension) instead of {formatCurrency(pensionComparison.totalKeptWithout)}.
                  </Typography>
                </Stack>
              </Paper>
              <Paper
                variant="outlined"
                sx={{ p: 2, height: { xs: 'auto', md: '100%' }, display: 'flex', flexDirection: 'column' }}
              >
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Why it bites
                  </Typography>
                  <Typography variant="body2">
                    Every £2 over £100k kills £1 of allowance. That £1 then gets taxed at 40%, adding an extra 20% on top of the higher rate, a 60% marginal hit.
                  </Typography>
                </Stack>
              </Paper>
            </Box>
          </Stack>
        </Paper>

        <Paper elevation={3} sx={{ p: 3, mt: 3, borderRadius: 3 }}>
          <Typography variant="h6" gutterBottom>
            One view: how much you actually keep
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Orange shows take-home if you let the extra earnings flow through tax. Blue shows take-home with your current pension input. Green dashed is "take-home + pension", the total you keep.
          </Typography>
          <Box sx={{ height: 420 }}>
            <Line data={lineData} options={lineOptions} />
          </Box>
          <Stack spacing={0.75} sx={{ mt: 2 }}>
            <Typography variant="body2">
              At £{income.toLocaleString('en-GB')}, you keep {formatCurrency(results.takeHome)} after tax.
            </Typography>
            <Typography variant="body2">
              If you instead pension {formatCurrency(pensionContribution || 0)}, your combined "take-home + pension" is {formatCurrency(pensionComparison.totalKeptWithPension)} vs {formatCurrency(pensionComparison.totalKeptWithout)} with no pension.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Taper is currently costing {formatCurrency(taperTakeHomeGap)} versus having the full allowance.
            </Typography>
          </Stack>
        </Paper>
      </Container>
    </>
  );
};

export default AllowanceTrapApp;
